/**
 * LaTeX math for the Tiptap markdown editor.
 *
 * `@tiptap/extension-mathematics` ships the schema, KaTeX NodeViews, input
 * rules, *and* markdown round-tripping (`parseMarkdown` / `renderMarkdown` /
 * `markdownTokenizer`) for the official `@tiptap/markdown` extension. We keep
 * everything except its tokenizers, which are too eager for prose that
 * mentions money: the stock inline rule `\$([^$]+)\$` happily turns
 * `it cost $5 and $10` into a math atom (`5 and`), silently rewriting the
 * document on the next save.
 *
 * These `.extend()`s swap in the canonical `markdown-it-katex` delimiter
 * scanner (@waylonflinn), ported to marked's tokenizer contract:
 *
 *   - inline `$…$`: the closing `$` must not be preceded by whitespace and
 *     must not be followed by a digit, so `$5 and $10` stays prose; `\$`
 *     escapes survive because the closing scan counts trailing backslashes.
 *   - block `$$…$$`: opens at the start of a line, closes at a line ending
 *     in `$$`, and may span multiple lines. An unterminated `$$` fence
 *     matches nothing (stays literal text) instead of swallowing the rest of
 *     the document — which would otherwise get re-emitted with a synthetic
 *     closing fence on the next save.
 *
 * The tokens carry the same `{ type, raw, latex }` shape the base
 * extensions' `parseMarkdown` expects, so parsing and serialization are
 * inherited untouched.
 */
import type { MarkdownToken } from "@tiptap/core";
import { BlockMath, InlineMath } from "@tiptap/extension-mathematics";

function isWhitespace(ch: string | undefined): boolean {
  return ch === " " || ch === "\t";
}

function isDigit(ch: string | undefined): boolean {
  return ch !== undefined && ch >= "0" && ch <= "9";
}

/** True when the `$` at `index` is escaped by an odd run of backslashes. */
function isEscaped(src: string, index: number): boolean {
  let backslashes = 0;
  for (let i = index - 1; i >= 0 && src[i] === "\\"; i--) backslashes++;
  return backslashes % 2 === 1;
}

/**
 * Tokenize inline `$…$` at the start of `src`. Returns undefined when the
 * `$` doesn't open a valid math span, leaving the character to marked's
 * literal-text fallback — the same net effect as markdown-it's
 * `pending += "$"`.
 */
export function tokenizeInlineMath(src: string): MarkdownToken | undefined {
  if (src[0] !== "$") return undefined;
  // `$$` is block math (or literal), and an opening `$` followed by
  // whitespace can't open a span.
  if (src[1] === "$" || isWhitespace(src[1]) || src[1] === undefined) {
    return undefined;
  }

  // Scan for the matching closing `$`, skipping escaped ones.
  let close = src.indexOf("$", 1);
  while (close !== -1 && isEscaped(src, close)) {
    close = src.indexOf("$", close + 1);
  }
  if (close === -1) return undefined;

  // A closing `$` preceded by whitespace, or followed by a digit (so `$5
  // and $10` reads as currency, not math), can't close.
  if (isWhitespace(src[close - 1]) || isDigit(src[close + 1])) {
    return undefined;
  }

  const latex = src.slice(1, close);
  if (!latex.trim()) return undefined;
  return { type: "inlineMath", raw: src.slice(0, close + 1), latex };
}

/**
 * Tokenize block `$$…$$` at the start of `src` (marked hands block
 * tokenizers the remaining source anchored at a line start). Handles the
 * single-line form `$$x$$` and multi-line fences closed by a line ending in
 * `$$`. Returns undefined for an unterminated fence.
 */
export function tokenizeBlockMath(src: string): MarkdownToken | undefined {
  if (!src.startsWith("$$")) return undefined;

  const newline = src.indexOf("\n");
  const firstLine = (
    newline === -1 ? src.slice(2) : src.slice(2, newline)
  ).trimEnd();

  // Single-line `$$ … $$`. A line with *interior* `$$` pairs beyond the
  // closing fence (`$$ x $$ and $$ y $$`) is ambiguous — matching it would
  // swallow the prose between the pairs into one LaTeX atom and re-serialize
  // it as display math. Leave the whole line literal instead.
  if (firstLine.endsWith("$$")) {
    const latex = firstLine.slice(0, -2).trim();
    if (latex.includes("$$")) return undefined;
    const rawEnd = newline === -1 ? src.length : newline + 1;
    return { type: "blockMath", raw: src.slice(0, rawEnd), latex };
  }
  if (newline === -1) return undefined;

  // Multi-line: collect lines until one ends with `$$`.
  const lines: string[] = firstLine.trim() ? [firstLine] : [];
  let lineStart = newline + 1;
  while (lineStart <= src.length) {
    const lineEnd = src.indexOf("\n", lineStart);
    const line =
      lineEnd === -1 ? src.slice(lineStart) : src.slice(lineStart, lineEnd);
    const trimmed = line.trimEnd();
    if (trimmed.endsWith("$$")) {
      const beforeFence = trimmed.slice(0, trimmed.lastIndexOf("$$"));
      if (beforeFence.trim()) lines.push(beforeFence.trimEnd());
      const rawEnd = lineEnd === -1 ? src.length : lineEnd + 1;
      return {
        type: "blockMath",
        raw: src.slice(0, rawEnd),
        latex: lines.join("\n").trim(),
      };
    }
    lines.push(line);
    if (lineEnd === -1) break;
    lineStart = lineEnd + 1;
  }
  // Unterminated fence — stay literal text.
  return undefined;
}

/** Inline `$…$` math with a currency-safe markdown tokenizer. */
export const InlineMathMarkdown = InlineMath.extend({
  markdownTokenizer: {
    name: "inlineMath",
    level: "inline",
    start: (src: string) => src.indexOf("$"),
    tokenize: tokenizeInlineMath,
  },
});

/** Block `$$…$$` (display) math with an unterminated-fence-safe tokenizer. */
export const BlockMathMarkdown = BlockMath.extend({
  markdownTokenizer: {
    name: "blockMath",
    level: "block",
    start: (src: string) => src.indexOf("$$"),
    tokenize: tokenizeBlockMath,
  },
});
